/**
 * ConfirmationDialog.tsx - Reusable Confirmation Dialog Component
 *
 * A reusable dialog component for confirming destructive actions
 */

import i18n from "@/i18n/config";
import { Button, Text } from "@chakra-ui/react";
import { DialogRoot, DialogBackdrop, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogActionTrigger, DialogCloseTrigger, DialogTitle } from "@chakra-ui/react";

interface ConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    confirmColorScheme?: string;
    titleColor?: string;
}

export const ConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    confirmColorScheme = "red",
    titleColor = "var(--chakra-colors-red-500)"
}: ConfirmationDialogProps) => {
    if (!open) return null;

    const isRTL = i18n.dir() === 'rtl';
    const textAlign = isRTL ? 'right' : 'left';

    return (
        <DialogRoot open={open} onOpenChange={(e) => !e.open && onClose()}>
            <DialogBackdrop
                css={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9998,
                    background: 'rgba(0, 0, 0, 0.5)',
                }}
            />
            <DialogContent
                dir={i18n.dir()}
                css={{
                    position: 'fixed',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999,
                    background: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                }}
            >
                <DialogHeader>
                    <DialogTitle textAlign={textAlign} css={{ fontSize: '16px', color: titleColor }}>
                        {title}
                    </DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <Text textAlign={textAlign}>{message}</Text>
                </DialogBody>
                <DialogFooter
                    css={{
                        justifyContent: isRTL ? 'flex-start' : 'flex-end',
                        flexDirection: isRTL ? 'row-reverse' : 'row',
                    }}
                >
                    <DialogActionTrigger asChild>
                        <Button variant="outline" onClick={onClose}>
                            {cancelText}
                        </Button>
                    </DialogActionTrigger>
                    <Button colorScheme={confirmColorScheme} onClick={onConfirm}>
                        {confirmText}
                    </Button>
                </DialogFooter>
                <DialogCloseTrigger />
            </DialogContent>
        </DialogRoot>
    );
};
